import { Plus } from 'lucide-react';
import {
  Notification,
  Notification as NotificationType,
  NotificationFilter,
} from 'modules/core/domain/interfaces/supabase/notifications/types';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import UDButton from 'ud-ui/components/button';
import UDChip from 'ud-ui/components/chip';
import UDHeaderTitle from 'ud-ui/components/header-title';
import UDText from 'ud-ui/components/text';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AppDispatch } from 'store/store';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { useMainLayout } from 'ud-ui/layout/main/context';
import { NotificationFilters } from '../../domain/interface/notification';
import { hasNewNotifications } from '../../domain/utils/has-new-notifications';
import { deleteNotification, fetchNotifications } from '../../store/actions';
import { setFilters, setOffset } from '../../store/notifications';
import {
  selectHasMoreNotifications,
  selectNotifications,
  selectNotificationsError,
  selectNotificationsFilters,
  selectNotificationsLoading,
  selectNotificationsTotal,
} from '../../store/selectors';
import NotificationComponent from '../components/notification';
import NotificationSkeleton from '../components/notification-skeleton';
import NotificationsFilters from '../components/notifications-filters';
import ImgNotFound from 'ud-ui/components/images/not-found.png';
import useUnreadNotifications from '../hooks/use-unread-notifications';
import Modal from 'ud-ui/components/modal';

const ITEMS_PER_PAGE = 10;

const NotificationsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useSelector(selectNotifications);
  const loading = useSelector(selectNotificationsLoading);
  const error = useSelector(selectNotificationsError);
  const total = useSelector(selectNotificationsTotal);
  const filters = useSelector(selectNotificationsFilters);
  const hasMore = useSelector(selectHasMoreNotifications);
  const isAdmin = useIsAdmin();
  const { info } = useOrganizer();
  const organizerId = info?.id;
  const [pendingDeleteId, setPendingDeleteId] = React.useState<number | null>(null);
  const { setHeaderContent } = useMainLayout();

  // const { Modal, defaultOpen: openDeleteModal, close: closeDeleteModal } = useModal();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openDeleteModal = useCallback(() => setIsModalOpen(true), []);
  const closeDeleteModal = useCallback(() => setIsModalOpen(false), []);

  const getApiFilters = useCallback((): NotificationFilter => {
    return {
      type: filters.type === 'all' ? undefined : filters.type,
      userId: organizerId,
      limit: ITEMS_PER_PAGE,
      offset: filters.offset,
      sort: filters.sort,
    };
  }, [filters, organizerId]);

  useEffect(() => {
    const apiFilters = getApiFilters();
    dispatch(fetchNotifications(apiFilters));
  }, [dispatch, getApiFilters]);

  const handleFilterChange = (newFilters: NotificationFilters) => {
    dispatch(setFilters({ ...newFilters, limit: ITEMS_PER_PAGE, userId: organizerId }));
  };

  const navigate = useNavigate();

  const fetchMore = useCallback(() => {
    if (!hasMore) {
      return;
    }
    if (notifications.length < total) {
      dispatch(setOffset((filters.offset ?? 0) + ITEMS_PER_PAGE));
    }
  }, [notifications.length, total, dispatch, filters.offset, hasMore]);

  // Бесконечная прокрутка
  useEffect(() => {
    const handleScroll = () => {
      if (loading || notifications.length >= total || !hasMore) {
        return;
      }
      const scrollPosition = window.scrollY + window.innerHeight;
      const bottomPosition = document.documentElement.scrollHeight;
      if (bottomPosition - scrollPosition < 200) {
        fetchMore();
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [fetchMore, loading, notifications.length, total, hasMore]);

  // Обработчики для админа
  const handleEdit = useCallback(
    (notification: Notification) => {
      navigate(`/notifications/edit/${notification.id}`);
    },
    [navigate],
  );

  const handleDelete = useCallback(
    (id: number) => {
      setPendingDeleteId(id);
      openDeleteModal();
    },
    [openDeleteModal],
  );

  const handleCancelDelete = useCallback(() => {
    setPendingDeleteId(null);
    closeDeleteModal();
  }, [closeDeleteModal]);

  const handleConfirmDelete = useCallback(async () => {
    if (pendingDeleteId == null) {
      return;
    }
    if (!isAdmin) {
      return;
    }
    try {
      await dispatch(deleteNotification(pendingDeleteId)).unwrap();
      toast.success('Уведомление удалено');
    } catch (error) {
      toast.error('Ошибка удаления уведомления');
    } finally {
      setPendingDeleteId(null);
      closeDeleteModal();
    }
  }, [pendingDeleteId, isAdmin, dispatch, closeDeleteModal]);

  const handleCreate = useCallback(() => {
    navigate('/notifications/create');
  }, [navigate]);

  // Хук непрочитанных: глобально помечает как прочитанные после загрузки (только на странице)
  const { markAllAsRead } = useUnreadNotifications({ autoFetch: false, markOnLoad: true });

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsRead();
  }, [markAllAsRead]);

  // Новые уведомления (Chip)
  const showNewChip = hasNewNotifications(notifications);

  useEffect(() => {
    setHeaderContent(
      <div className="d-flex align-items-center flex-column">
        <UDHeaderTitle>Уведомления</UDHeaderTitle>
      </div>,
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent, showNewChip]);

  return (
    <>
      <div className="h-100 flex-column container p-6" style={{ margin: '0 auto' }}>
        <div className="d-flex justify-content-between align-items-end mb-4">
          <NotificationsFilters filters={filters} onChange={handleFilterChange} />
          <div className="d-flex align-items-center gap-2">
            <UDButton variant="gray" onClick={handleMarkAllAsRead}>
              Прочитать все
            </UDButton>
            {isAdmin && (
              <UDButton variant="primary" onClick={handleCreate} className="d-flex align-items-center gap-2">
                <Plus size={20} />
                Создать
              </UDButton>
            )}
          </div>
        </div>
        <div className="flex-column">
          {loading && !notifications.length && Array.from({ length: 5 }).map((_, i: number) => <NotificationSkeleton key={i} />)}
          {!loading && !notifications.length && !error && (
            <div className="flex-column align-items-center justify-content-center flex-grow-1 py-8">
              <img style={{ width: 130, height: 130 }} alt={'not found'} src={ImgNotFound} />
              <UDText type="subhead">Уведомлений нет</UDText>
            </div>
          )}
          {error && (
            <div className="flex-column align-items-center justify-content-center h-100 py-8">
              <UDChip color="gray" size={32} className="mb-4" aria-label="Ошибка" />
              <UDText type="subhead">Ошибка загрузки: {error}</UDText>
            </div>
          )}
          {notifications.map((notification: NotificationType) => (
            <NotificationComponent
              key={notification.id}
              notification={notification}
              isNew={hasNewNotifications([notification])}
              allowChange={isAdmin}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
          {loading && notifications.length > 0 && <NotificationSkeleton />}

          <Modal show={isModalOpen} onClose={closeDeleteModal}>
            <Modal.Body>
              <UDText type="headline" className="color-DarkBlue w-100 flex-center">
                Удалить уведомление
              </UDText>
              <UDText className="mt-2" type="subhead">
                Вы уверены, что хотите удалить это уведомление?
              </UDText>
              <UDText type="subhead">Это действие нельзя отменить.</UDText>
            </Modal.Body>
            <Modal.Footer>
              <UDButton variant="secondary" onClick={handleCancelDelete} stretch loading={loading}>
                Отмена
              </UDButton>
              <UDButton variant="primary" onClick={handleConfirmDelete} stretch loading={loading}>
                Удалить
              </UDButton>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default NotificationsPage;
